import { track } from "./telemetry";

/**
 * LO QUE EL USUARIO VE Y EL DUEÑO NO — errores del cliente al panel.
 *
 * El backend registra lo que le llega (500, 400, 401/403 con token…), pero hay
 * una clase entera que NUNCA le llega: la llamada que murió antes de salir del
 * teléfono. Sin señal en la cancha, DNS caído, servidor inalcanzable, y las
 * excepciones de la propia pantalla. Para el panel esas fallas no existían;
 * para el que está parado en la cancha son las únicas que existen.
 *
 * Viaja por la telemetría que ya está hecha (`app_events`, migración 056): no
 * hace falta endpoint ni tabla nueva. Y como el buffer de telemetría guarda lo
 * que no pudo mandar por falta de sesión, un error de "sesión caída" llega
 * igual cuando la persona vuelve a entrar.
 *
 * Lo que NO cubre, dicho de frente: si el navegador se cierra sin volver a
 * abrirse, o si nunca vuelve a haber red, el evento se pierde. Persistirlo en
 * disco es la versión completa; esto es la mínima útil.
 */

/** Techo por sesión: un cliente en loop no puede llenar la tabla. */
const MAX_POR_SESION = 20;
/** Misma falla repetida: se cuenta una sola vez (con su total al final). */
const vistos = new Map<string, number>();
let enviados = 0;

type FalloApi = {
  status: number;
  code: string;
  ruta: string;
  metodo: string;
  mensaje: string;
};

/** Mensajes largos (stack traces, SQL) no entran en un panel ni sirven. */
function recortar(texto: string): string {
  return texto.replace(/\s+/g, " ").trim().slice(0, 160);
}

function reportar(tipo: string, props: Record<string, string | number>) {
  const clave = `${tipo}|${props.ruta ?? ""}|${props.code ?? props.mensaje ?? ""}`;
  const repeticiones = (vistos.get(clave) ?? 0) + 1;
  vistos.set(clave, repeticiones);
  // La primera vez se manda; las repeticiones sólo suben el contador, y se
  // vuelve a mandar en potencias de 10 para no perder de vista una falla que
  // se disparó 300 veces.
  const hito = repeticiones === 1 || repeticiones === 10 || repeticiones === 100;
  if (!hito || enviados >= MAX_POR_SESION) return;
  enviados += 1;
  track("client_error", { ...props, tipo, repeticiones });
}

let instalado = false;

/**
 * Se llama una vez al arrancar la app (main.tsx), NO por pantalla: cubre tanto
 * el panel del organizador como la PWA del jugador.
 */
export function installClientErrorReporting() {
  if (instalado || typeof window === "undefined") return;
  instalado = true;

  window.addEventListener("forja:api-error", (evento) => {
    const d = (evento as CustomEvent<FalloApi>).detail;
    if (!d) return;
    // Los 401 sin sesión son rutina (entrar a una pantalla con el token
    // vencido); el backend ya registra los que llegan CON token. Acá nos
    // importa lo que el backend no puede ver.
    if (d.status === 401) return;
    reportar(d.status === 0 ? "sin_red" : "http", {
      ruta: d.ruta,
      metodo: d.metodo,
      status: d.status,
      code: d.code,
      mensaje: recortar(d.mensaje),
    });
  });

  // Excepción no atrapada en la pantalla: la app se ve rota aunque el backend
  // haya respondido bien.
  window.addEventListener("error", (evento) => {
    if (!evento.message) return;
    reportar("pantalla", {
      ruta: window.location.pathname,
      mensaje: recortar(evento.message),
    });
  });

  // Promesa rechazada sin catch: el caso típico de un await suelto.
  window.addEventListener("unhandledrejection", (evento) => {
    const razon = evento.reason;
    reportar("promesa", {
      ruta: window.location.pathname,
      mensaje: recortar(razon instanceof Error ? razon.message : String(razon ?? "")),
    });
  });
}
